import dayjs from 'dayjs';
import { useEffect, useState } from 'react';

import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { initializeTemplateWithMetadata } from '../../utils/initializeTemplateWithMetadata';
import { getOnePagerTemplateData, saveOnePagerTemplateData } from '../../store/investor/investorApiHelper';
import { fetchCompanyOverviewData } from '../../store/dashboard/overview/actions';
import { OverviewData } from '../../store/dashboard/overview/types';
import { DATE_FORMAT } from '../../utils/constants/commonConstants';

import { Button, Modal } from '../_custom';
import DocumentEditor from './DocumentEditor';
import { sanitizeSlateValue } from '../TextEditor/utils/SlateSerialisationUtil';

import styles from './DocumentContainer.module.scss';

type TemplateMetadata = Parameters<typeof initializeTemplateWithMetadata>[1];

interface DocumentContainerProps {
    // Redux-sourced data
    orgId: string;
    source: string;
    loading: boolean;
    companyName: string;

    // Redux action callbacks
    onUnmount: () => void;
    setLoading: (loading: boolean) => void;
    onSaveComplete: () => void;

    // API
    fetchTemplate: (params: { orgId: string; accountType: string; pnoId: string; primaryEmail: string }) => Promise<any>;

    // Save config
    docType: 'NDA' | 'ENGAGEMENT_LETTER' | 'ONE_PAGER' | 'BUREAU_KYC_CONSENT';

    // Optional: fetch extra template metadata (e.g. entityType for NDA)
    fetchExtraMetadata?: (orgId: string) => Promise<Partial<TemplateMetadata>>;

    // Regen modal
    regenTitle: string;

    // DocumentEditor config
    editorType: string;
    documentType: 'nda' | 'engagementLetter' | 'onePager' | 'bureauKycConsent';
    fileNamePrefix: string;
    commentContainerId: string;
    mixpanelPreviewEvent: string;
    mixpanelSaveEvent: string;
}

function DocumentContainer({
    orgId,
    source,
    loading,
    companyName,
    onUnmount,
    setLoading,
    onSaveComplete,
    fetchTemplate,
    docType,
    fetchExtraMetadata,
    regenTitle,
    editorType,
    documentType,
    fileNamePrefix,
    commentContainerId,
    mixpanelPreviewEvent,
    mixpanelSaveEvent,
}: DocumentContainerProps) {
    const dispatch = useAppDispatch();
    const [savingData, setSavingData] = useState(false);
    const [userInfo, setUserInfo] = useState({});
    const [editorInitialData, setEditorInitialData] = useState<any>(null);
    const [editorReady, setEditorReady] = useState(false);
    const [showRegenConfirmModal, setShowRegenConfirmModal] = useState(false);

    const companyMainImg = useAppSelector((state) => state.user.domainMetadata?.mainImageUrl);
    const investorOrgId = useAppSelector((state) => state.user.userData.investorOrgId);
    const emailId = useAppSelector((state) => state.user.userData.primaryEmail);
    const userName = useAppSelector((state) => state.user.userData.userName);
    const userJobRole = useAppSelector((state) => state.user.userData.userJobRole);

    useEffect(() => {
        return () => {
            onUnmount();
        };
    }, []);

    useEffect(() => {
        fetchTemplateData();
    }, [orgId]);

    useEffect(() => {
        if (editorInitialData) {
            setEditorReady(true);
        }
    }, [editorInitialData]);

    const fetchDataForDoc = async (isRegenerate = false) => {
        if (!isRegenerate) {
            setEditorReady(false);
        }
        setLoading(true);
        dispatch(
            fetchCompanyOverviewData({
                assesseeOrgId: orgId,
                investorOrgId: investorOrgId as string,
                onSuccess: async (data: OverviewData) => {
                    const [template, extraMeta] = await Promise.all([
                        fetchTemplate({
                            orgId,
                            accountType: 'INVESTOR',
                            pnoId: investorOrgId as string,
                            primaryEmail: emailId as string,
                        }),
                        fetchExtraMetadata ? fetchExtraMetadata(orgId) : Promise.resolve({}),
                    ]);
                    setEditorInitialData(
                        sanitizeSlateValue(
                            initializeTemplateWithMetadata(template || [], {
                                pnoLogo: companyMainImg as string,
                                companyName: companyName ?? '',
                                companyAddress: data?.headquarterLocation?.fullAddress ?? '',
                                userName: userName ?? '',
                                userDesignation: userJobRole ?? '',
                                userEmail: emailId ?? '',
                                currentDate: dayjs().format(DATE_FORMAT),
                                ...extraMeta,
                            }),
                        ),
                    );
                    setEditorReady(true);
                    setLoading(false);
                },
                onError: () => {
                    setEditorReady(true);
                    setLoading(false);
                },
            }),
        );
    };

    const fetchTemplateData = () => {
        let timeout: NodeJS.Timeout;
        setEditorReady(false);
        setLoading(true);
        getOnePagerTemplateData({
            assesseeOrgId: orgId,
            investorOrgId,
            docType,
        })
            .then(({ onePagerData, templateData }) => {
                if (templateData?.length) {
                    setEditorInitialData(sanitizeSlateValue(templateData));
                    setUserInfo(onePagerData);
                    timeout = setTimeout(() => {
                        setEditorReady(true);
                        setLoading(false);
                    }, 200);
                } else {
                    fetchDataForDoc();
                }
            })
            .catch(() => {});
        return () => clearTimeout(timeout);
    };

    const saveDoc = (editorContent) => {
        if (savingData) return;
        setSavingData(true);
        saveOnePagerTemplateData({
            assesseeOrgId: orgId,
            investorOrgId,
            onePagerData: editorContent || [],
            email: emailId as string,
            docType,
        })
            .then(() => {
                setTimeout(() => {
                    onSaveComplete();
                    setSavingData(false);
                }, 1000);
            })
            .catch(() => {
                setSavingData(false);
            });
    };

    return (
        <div>
            {editorReady && (
                <DocumentEditor
                    source={source}
                    generating={loading}
                    openModal={editorReady}
                    orgId={orgId}
                    orgName={companyName}
                    editorInitialData={editorInitialData}
                    editorReady={editorReady}
                    savingData={savingData}
                    saveDoc={saveDoc}
                    setShowRegenConfirmModal={setShowRegenConfirmModal}
                    userInfo={userInfo}
                    editorType={editorType}
                    documentType={documentType}
                    fileNamePrefix={fileNamePrefix}
                    commentContainerId={commentContainerId}
                    mixpanelPreviewEvent={mixpanelPreviewEvent}
                    mixpanelSaveEvent={mixpanelSaveEvent}
                />
            )}

            {showRegenConfirmModal && (
                <Modal open={showRegenConfirmModal} onClose={() => setShowRegenConfirmModal(false)} size="flexible">
                    <div className={styles.ConfirmationModal}>
                        <div className={styles.Title}>Regenerate {regenTitle}</div>
                        <div className={styles.Subtext}>
                            Are you sure you want to regenerate {regenTitle}? You will lose all the changes made so far.
                        </div>
                        <div className={styles.Buttons}>
                            <Button
                                text={'Cancel'}
                                onClick={() => setShowRegenConfirmModal(false)}
                                variant={'purpleinverted'}
                                style={{ marginTop: '0' }}
                            />
                            <Button
                                text={'Proceed Anyway'}
                                onClick={() => {
                                    setShowRegenConfirmModal(false);
                                    fetchDataForDoc(true);
                                }}
                                style={{ marginTop: '0' }}
                                variant={'primary'}
                            />
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
}

export default DocumentContainer;
